import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Lock } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useCompanyLogo } from "@/hooks/use-company-logo";

interface AdminLoginProps {
  onLoginSuccess: () => void;
}

export default function AdminLogin({ onLoginSuccess }: AdminLoginProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const { toast } = useToast();
  const logoUrl = useCompanyLogo();

  const { data: adminExists, refetch: refetchAdminExists } = useQuery<{ exists: boolean }>({
    queryKey: ["/api/admin-auth/exists"],
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await apiRequest("POST", "/api/admin-auth/login", { username, password });
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Inloggad",
          description: `Välkommen, ${data.admin.displayName}!`,
        });
        setTimeout(() => {
          window.location.href = "/admin/dashboard";
        }, 300);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Inloggning misslyckades",
        description: error.message || "Fel användarnamn eller lösenord",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInitializeAdmin = async () => {
    setIsInitializing(true);
    try {
      const response = await apiRequest("POST", "/api/admin-auth/init", {});
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Admin skapad",
          description: "Användarnamn: admin, Lösenord: admin123",
        });
        setUsername("admin");
        setPassword("admin123");
        refetchAdminExists();
      } else if (data.exists) {
        toast({
          title: "Admin finns redan",
          description: "Logga in med dina uppgifter",
        });
        refetchAdminExists();
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fel",
        description: error.message || "Kunde inte skapa admin",
      });
    } finally {
      setIsInitializing(false);
    }
  };

  const showInitButton = adminExists && !adminExists.exists;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={logoUrl} alt="Rätt Order" className="h-12" />
          </div>
          <CardTitle className="text-2xl">Admin Inloggning</CardTitle>
          <CardDescription>
            Logga in för att hantera systemet
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Användarnamn</Label>
              <Input
                id="username"
                data-testid="input-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Lösenord</Label>
              <Input
                id="password"
                data-testid="input-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="button-login"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loggar in...
                </>
              ) : (
                "Logga in"
              )}
            </Button>
          </form>
          
          {showInitButton && (
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-muted-foreground text-center mb-4">
                Första gången? Skapa admin-konto:
              </p>
              <Button
                type="button"
                variant="outline"
                data-testid="button-init-admin"
                className="w-full"
                onClick={handleInitializeAdmin}
                disabled={isInitializing}
              >
                {isInitializing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Skapar admin...
                  </>
                ) : (
                  "Skapa standard admin"
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Standarduppgifter: admin / admin123
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
